/** Shared recipe parameter grouping for workflow editor and recipe detail views. */

export type WoRowLite = {
  id?: number;
  opterionid?: string | null;
  cycletime?: string | null;
  setuptime?: string | null;
  active?: boolean;
  processDefinitionName?: string | null;
  mfgTechnologyName?: string | null;
  manufacturingTechnologyId?: string | null;
  machineModelId?: string | null;
  synthetic?: boolean;
};

export type RecipeParamDef = {
  key: string;
  label: string;
  required?: boolean;
  unit?: string;
  defaultValue?: string;
  minValue?: string;
  maxValue?: string;
};

export type RecipeGroupDef = { title: string; params: RecipeParamDef[] };

export const RECIPE_GROUPS_CNC: RecipeGroupDef[] = [
  {
    title: "SPEED & FEED",
    params: [
      { key: "spindle", label: "Spindle Speed", required: true, unit: "RPM" },
      { key: "feed", label: "Feed Rate", required: true, unit: "mm/min" },
      { key: "doc", label: "Depth of Cut", unit: "mm" },
      { key: "dir", label: "Cutting Direction" },
    ],
  },
  {
    title: "COOLANT",
    params: [
      { key: "cp", label: "Coolant Pressure", unit: "bar" },
      { key: "ce", label: "Coolant Enabled", required: true },
    ],
  },
  {
    title: "QUALITY",
    params: [{ key: "sf", label: "Surface Finish Target", required: true, unit: "Ra µm" }],
  },
];

export const RECIPE_GROUPS_SURFACE: RecipeGroupDef[] = [
  {
    title: "CHEMISTRY",
    params: [
      { key: "conc", label: "Solution Concentration", unit: "%" },
      { key: "ph", label: "pH Level", unit: "pH" },
      { key: "acid", label: "Acid Type" },
    ],
  },
  {
    title: "TEMPERATURE",
    params: [{ key: "bath", label: "Bath Temperature", unit: "°C" }],
  },
  { title: "TIME", params: [{ key: "imm", label: "Immersion Time", unit: "min" }] },
  {
    title: "ELECTRICAL",
    params: [{ key: "cd", label: "Current Density", unit: "A/dm²" }],
  },
  {
    title: "QUALITY",
    params: [{ key: "coat", label: "Coating Thickness", unit: "µm" }],
  },
];

export const RECIPE_GROUPS_INSPECTION: RecipeGroupDef[] = [
  { title: "FORCE", params: [{ key: "pf", label: "Probe Force", required: true, unit: "mN" }] },
  { title: "SPEED", params: [{ key: "ss", label: "Scan Speed", required: true, unit: "mm/s" }] },
  {
    title: "QUALITY",
    params: [
      { key: "mu", label: "Measurement Uncertainty", unit: "µm" },
      { key: "grr", label: "Repeatability (GR&R)", unit: "%" },
      { key: "ar", label: "Accept/Reject Criteria" },
    ],
  },
  { title: "PLAN", params: [{ key: "ssz", label: "Sampling Size", required: true, unit: "%" }] },
  { title: "REFERENCE", params: [{ key: "ms", label: "Measurement Standard" }] },
];

export function isCmmProcessName(name: string | null | undefined): boolean {
  return /cmm\s*inspection/i.test(String(name ?? "").trim());
}

export function applyDefaultCellsFromGroups(
  cells: Record<string, string>,
  stepIndex: number,
  groups: RecipeGroupDef[],
): Record<string, string> {
  const out = { ...cells };
  for (const g of groups) {
    for (const p of g.params) {
      const tKey = `${stepIndex}-${p.key}-t`;
      const nKey = `${stepIndex}-${p.key}-n`;
      const xKey = `${stepIndex}-${p.key}-x`;
      if (out[tKey] === undefined || out[tKey] === "") out[tKey] = p.defaultValue ?? "";
      if (out[nKey] === undefined || out[nKey] === "") out[nKey] = p.minValue ?? "";
      if (out[xKey] === undefined || out[xKey] === "") out[xKey] = p.maxValue ?? "";
    }
  }
  return out;
}

export function resolveRecipeGroupsForStep(
  stepGroups: RecipeGroupDef[][] | null | undefined,
  stepIndex: number,
  op: WoRowLite,
): RecipeGroupDef[] {
  if (stepGroups?.[stepIndex]?.length) return stepGroups[stepIndex]!;
  return recipeGroupsForStep(op);
}

export function isDynamicRecipeParamKey(key: string): boolean {
  return key.startsWith("pp-");
}

export function recipeGroupsForStep(op: WoRowLite): RecipeGroupDef[] {
  const tech = (op.mfgTechnologyName ?? "").toLowerCase();
  const proc = (op.processDefinitionName ?? "").toLowerCase();
  if (isCmmProcessName(op.processDefinitionName) || proc.includes("cmm")) {
    return RECIPE_GROUPS_INSPECTION;
  }
  if (tech.includes("surface") || tech.includes("treatment") || tech.includes("chemical")) {
    return RECIPE_GROUPS_SURFACE;
  }
  return RECIPE_GROUPS_CNC;
}

export function buildDisplayWorkflowOps(detailOps: WoRowLite[]): WoRowLite[] {
  const sorted = [...detailOps].filter((o) => o.active !== false && !o.synthetic);
  sorted.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
  return sorted;
}

/** Param keys that count as "critical" when a target cell is filled (matches editor *-required). */
export const CRITICAL_RECIPE_PARAM_KEYS = new Set([
  "spindle",
  "feed",
  "sf",
  "ce",
  "pf",
  "ss",
  "ssz",
]);

export function countCriticalTargets(
  cells: Record<string, string>,
  _keysSet?: Set<string>,
): number {
  let n = 0;
  for (const [k, val] of Object.entries(cells)) {
    if (!/^(\d+)-([^-]+)-c$/.exec(k)) continue;
    if (String(val ?? '').trim() === '1') n += 1;
  }
  return n;
}

export function isParamCriticalMarked(
  cells: Record<string, string>,
  stepIndex: number,
  paramKey: string,
): boolean {
  return (cells[`${stepIndex}-${paramKey}-c`] ?? '').trim() === '1';
}

function parseNumericCell(raw: string | undefined): number | null {
  const s = String(raw ?? "").trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * When Target (t), Nominal (n), and Tolerance (x) are all numeric for a param:
 * require nominal ≥ target and tolerance ≥ nominal.
 */
export function validateRecipeTnmOrdering(cells: Record<string, string>): string[] {
  const seen = new Set<string>();
  const errors: string[] = [];
  for (const k of Object.keys(cells)) {
    const m = /^(\d+)-([^-]+)-t$/.exec(k);
    if (!m) continue;
    const step = m[1]!;
    const pk = m[2]!;
    const base = `${step}-${pk}`;
    if (seen.has(base)) continue;
    seen.add(base);
    const t = parseNumericCell(cells[`${base}-t`]);
    const n = parseNumericCell(cells[`${base}-n`]);
    const x = parseNumericCell(cells[`${base}-x`]);
    if (t === null || n === null || x === null) continue;
    const stepNum = Number(step);
    const stepLabel = Number.isFinite(stepNum) ? `Step ${stepNum + 1}` : `Step ${step}`;
    if (n < t) {
      errors.push(
        `${stepLabel} (${pk}): nominal (${n}) must be greater than or equal to target (${t}).`,
      );
    }
    if (x < n) {
      errors.push(
        `${stepLabel} (${pk}): tolerance (${x}) must be greater than or equal to nominal (${n}).`,
      );
    }
  }
  return errors;
}
